import { Injectable } from '@angular/core';

import { IAutoIngestJobInfo } from 'rev-shared/media/IAutoIngestJobInfo';
import { IWebexSiteSettings } from 'rev-shared/media/IWebexSiteSettings';
import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { IUnsubscribe } from 'rev-shared/push/IUnsubscribe';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { PushService, isPushError } from 'rev-shared/push/PushService';
import { PushBus } from 'rev-shared/push/PushBus.Service';

import { omit } from 'underscore';
import { HttpClient } from '@angular/common/http';
import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { map } from 'rxjs/operators';

import { AutoInjestType } from './mediaFeatures/AutoIngestLogs.Component';
import { IVideoAiSettings } from './MediaSettingsTypes';

export const NoProfileImagesForTheAccount = 'NoProfileImagesForTheAccount';
export const InvalidZoomSipAddressSuffix = 'InvalidZoomSipAddressSuffix';
export const TranscriptionVocabularyPending = 'TranscriptionVocabularyPending';
export const CustomDictionaryLanguagePhraseIsRequired = 'CustomDictionaryLanguagePhraseIsRequired';
export const CustomDictionaryLanguageNumberOfPhrasesExceedsLimit = 'CustomDictionaryLanguageNumberOfPhrasesExceedsLimit';

@Injectable({
	providedIn: 'root'
})
export class MediaSettingsService {

	private unsubscribePushListeners: IUnsubscribe;

	constructor(
		private readonly http: HttpClient,
		private readonly DateParsers: DateParsersService,
		private readonly Dialog: DialogService,
		private readonly MediaFeatures: MediaFeaturesService,
		private readonly PushBus: PushBus,
		private readonly PushService: PushService
	) {
	}

	public getIntegrationSettings(accountId: string): Promise<any> {
		return lastValueFrom(
			this.http.get(`/media/accounts/${accountId}/integration-settings`)
				.pipe(
					map((result: any) => ({
						...result,
						webcastSources: {
							vodEnabled: !result.webcastSources?.vodDisabled,
							webrtcSinglePresenterEnabled: !result.webcastSources?.webrtcSinglePresenterDisabled
						},
						webexSitesSettings: result.webexSitesSettings || [],
						webexEventsSettings: result.webexEventsSettings || {},
						msTeamsSettings: result.msTeamsSettings || {},
						vciSettings: result.vciSettings || {},
						sparkSettings: result.sparkSettings || {},
						zoomSettings: result.zoomSettings || {},
						pexipSettings: result.pexipSettings || {},
						boxSettings: result.boxSettings || {}
					}))
				)
		);
	}

	public getVideoAiSettings(accountId: string): Promise<IVideoAiSettings> {
		return lastValueFrom(
			this.http.get(`/media/accounts/${accountId}/ai-settings`)
				.pipe(
					map((result: any) => ({
						...result,
						enableFacialRecognition: result.enableFacialRecognition,
						enableVbrickAssistant: result.enableVbrickAssistant,
						enableVideoMetadataGeneration: result.enableVideoMetadataGeneration,
						enableContentAnalysis: result.enableContentAnalysis,
						enablePolicyAnalysis: result.enablePolicyAnalysis,
						enableAudioTrackGeneration: result.enableAudioTrackGeneration,
						enableMultimediaAnalysis: result.enableMultimediaAnalysis,
						transcriptionSettings: result.transcriptionSettings || {}
					}))
				)
		);
	}

	public getFeatureSettings(accountId: string): Promise<any> {
		return lastValueFrom(this.http.get(`/media/accounts/${accountId}/feature-settings`));
	}

	public getRecordingSettings(accountId: string): Promise<any> {
		return lastValueFrom(this.http.get(`/network/accounts/${accountId}/recording/settings`));
	}

	public saveFeatures(features: any): Promise<void> {
		return this.PushService.dispatchCommand('media:SaveFeatureSettings', features)
			.then(() => {
				this.MediaFeatures.updateAccountFeatures(features.accountId, {
					...omit(features, 'slideDelay'),
					enableSlideDelay: features.slideDelay.isEnabled
				});
			});
	}

	public saveIntegrationSettings(settings: any): Promise<void> {

		return this.PushService.dispatchCommand('media:SaveIntegrationSettings', settings)
			.then(() => {
				this.updateAccountFeatures(settings.accountId, {
					enableSpark: settings.sparkSettings.isEnabled,
					zoomSettings: settings.zoomSettings,
					vciSettings:  { ...settings.vciSettings, isMSTeamsEnabled: settings.msTeamsSettings.isVideoConferencingEnabled },
					webcastSources: settings.webcastSources,
					enableWebexEvents: settings.webexEventsSettings.isEnabled,
					pexipSettings: settings.pexipSettings,
					boxSettings: settings.boxSettings
				});
			})
			.catch(e => {
				const issue = isPushError(e) && e.issues[0];
				throw issue ? issue.id : e;
			});
	}

	public saveVideoAiSettings(settings: any) {
		return this.PushService.dispatchCommand('media:SaveAiSettings', settings)
			.then(() => {
				this.updateAccountFeatures(settings.accountId, {
					enableFacialRecognition: settings.enableFacialRecognition,
					enableVbrickAssistant: settings.enableVbrickAssistant,
					enableVideoMetadataGeneration: settings.enableVideoMetadataGeneration,
					enableContentAnalysis: settings.enableContentAnalysis,
					enablePolicyAnalysis: settings.enablePolicyAnalysis,
					enableAnswerEngine: settings.enableAnswerEngine,
					enableMultimediaAnalysis: settings.enableMultimediaAnalysis,
					enableAudioTrackGeneration: settings.enableAudioTrackGeneration,
					transcriptionSettings: settings.transcriptionSettings
				});
			})
			.catch(e => {
				const issue = isPushError(e) && e.issues[0];
				throw issue ? issue.id : e;
			});
	}

	public saveWebexSiteSettings(accountId: string, settings: any): Promise<void> {
		return this.PushService.dispatchCommand('media:SaveWebexSiteSettings', {
			accountId,
			webexSiteSettings: settings
		});
	}

	public deleteWebexSiteSettings(accountId: string, webexSiteSettingsId: string): Promise<void> {
		return this.PushService.dispatchCommand('media:RemoveWebexSiteSettings', {
			accountId,
			webexSiteSettingsId
		});
	}


	public saveRecordingSettings(accountId: string, recordingSettings: any): Promise<void> {
		return this.PushService.dispatchCommand('network:SaveRecordingConfiguration', {
			accountId,
			...recordingSettings
		});
	}

	public saveTemporaryWebexSettings(settings: any): Promise<void> {
		return this.PushService.dispatchCommand('media:SaveTemporaryWebexSettings', {
			webexSiteSettingsId: settings.id,
			webexCiSettings: settings.webexCiSettings
		});
	}

	public getAutoIngestLog(accountId: string, type: AutoInjestType, siteSettingsId?: string, ): Promise<IAutoIngestJobInfo[]> {
		let url = '';
		switch (type) {
			case AutoInjestType.WEBEX:
				url = `/media/accounts/${accountId}/webex/auto-ingest/${siteSettingsId}/logs`;
				break;
			case AutoInjestType.ZOOM:
				url = `/media/accounts/${accountId}/zoom/auto-ingest/logs`;
				break;
		}
		return lastValueFrom(this.http.get(url))
			.then(({ jobDetails }: any) => {
				for (const job of jobDetails) {
					job.whenRequested = this.DateParsers.parseUTCDate(job.whenRequested);
				}
				return jobDetails;
			});
	}

	public postponeAutoIngest(accountId: string, webexSiteId: string): Promise<any> {
		return lastValueFrom(this.http.post(`/media/accounts/${accountId}/webex/auto-ingest/${webexSiteId}/cancel`, {}));
	}

	public viewAutoIngestLog(accountId: string, type: AutoInjestType, siteSettingsId?: string): void {
		this.Dialog.getDialog('autoIngestLogs').open({
			initialState: {
				accountId,
				type,
				siteSettingsId
			}
		});
	}

	public registerPushHandlers(accountId: string, webexSitesSettings: IWebexSiteSettings[]): void {
		webexSitesSettings = [...webexSitesSettings];
		this.unsubscribePushListeners = this.PushBus.subscribe(accountId, {
			WebexSiteSettingsSaved: data => {
				this.updateWebexSitesSettingsCollection(accountId, data.webexSiteSettings, webexSitesSettings);
			}
		});
	}

	public unregisterPushHandlers(): void {
		if (this.unsubscribePushListeners) {
			this.unsubscribePushListeners();
		}
	}

	private updateWebexSitesSettingsCollection(accountId: string, settings: IWebexSiteSettings, webexSitesSettings: IWebexSiteSettings[]) {
		if (!settings) {
			return;
		}
		const index = webexSitesSettings.findIndex(site => site.id === settings.id);
		if (index >= 0) {
			const updatedData = {
				...webexSitesSettings[index],
				...settings
			};
			webexSitesSettings.splice(index, 1, updatedData);
		} else {
			webexSitesSettings = [...webexSitesSettings, settings];
		}
		this.updateAccountFeatures(accountId, { webexSitesSettings });

	}

	public setDefaultAutoIngestSettings(webexSettings: any): any {
		if (webexSettings.enableAutoIngest) {
			return webexSettings.autoIngestSettings;
		}

		return Object.assign(webexSettings.autoIngestSettings, {
			importPastRecordingDaysCount: 3,
			useEmailMatching: true,
			importAllRecordings: false,
			accessControl: 'Private',
			accessControlEntities: [],
			isActive: false
		});
	}

	private updateAccountFeatures(accountId: string, settings: any) {
		this.MediaFeatures.updateAccountFeatures(accountId, settings);
	}
}
